import React, { useState } from 'react';
import { useNetworkElementsStore } from '../../../../store/networkElementsStore';
import { useReadOnlyUi, useSteuerungEnabled } from '../../../../store/featureStore';
import { fmt } from '../../../../utils/numberFormat';
import {
  CONTROL_TRIGGER_LOADING_PCT,
  OPERATIONAL_RATING_FACTOR_DEFAULT,
  OPERATIONAL_RATING_FACTOR_MAX,
  OPERATIONAL_RATING_FACTOR_MIN,
  controlTriggerLoadingPct,
  isValidOperationalRatingFactor,
} from '../../../../utils/controlConstants';

interface Props {
  elementId: string;
  /** Stored factor; undefined = not maintained = 1,0. */
  factor: number | undefined;
  /** Genitive phrase of the rated value: "der Nennleistung" (trafo), "des Nennstroms" (cable). */
  ratingOf: string;
}

/**
 * Betriebsfaktor of a trafo or cable (migration 0061, STEUERUNG.md 7.2 Nr. 5): the operational continuous capacity
 * as a share of the rated one. The §14a trigger is 80 % of the operational capacity (FNN Hinweis 2c), so the card
 * shows the effective trigger next to the factor. Only visible with the control module (STEUERUNG_ENABLED);
 * editable unless the deployment is read-only. Compliance, Ampel and planning stay on the rated values.
 */
export const OperationalRatingFactorField: React.FC<Props> = ({ elementId, factor, ratingOf }) => {
  const steuerungEnabled = useSteuerungEnabled();
  const readOnly = useReadOnlyUi();
  const updateElement = useNetworkElementsStore(state => state.updateElement);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!steuerungEnabled) return null;

  const effective = factor ?? OPERATIONAL_RATING_FACTOR_DEFAULT;
  const draftValue = Number(draft.replace(',', '.'));
  const draftValid = draft.trim() !== '' && isValidOperationalRatingFactor(draftValue);

  const startEdit = () => {
    setDraft(fmt(effective, 2));
    setError(null);
    setEditing(true);
  };

  const save = async (value: number) => {
    setSaving(true);
    setError(null);
    const result = await updateElement(elementId, { operationalRatingFactor: value });
    setSaving(false);
    if (result.success) {
      setEditing(false);
    } else {
      setError('Speichern fehlgeschlagen. Der Betriebsfaktor muss zwischen '
        + `${fmt(OPERATIONAL_RATING_FACTOR_MIN, 1)} und ${fmt(OPERATIONAL_RATING_FACTOR_MAX, 1)} liegen.`);
    }
  };

  return (
    <div className="rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-gray-700 dark:text-primary-200">Betriebsfaktor (§ 14a)</div>
          <div className="text-[11px] text-gray-500 dark:text-primary-400">
            Betriebliche Dauerbelastbarkeit als Anteil {ratingOf}
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-primary-50" data-testid="operational-rating-factor">
              {fmt(effective, 2)}
            </span>
            {factor == null && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-primary-700 text-gray-500 dark:text-primary-300">Standard</span>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={startEdit}
                className="p-1 rounded text-gray-400 hover:text-info-600 dark:text-primary-400 dark:hover:text-info-400 hover:bg-gray-100 dark:hover:bg-primary-700 transition-colors"
                title="Betriebsfaktor bearbeiten"
                aria-label="Betriebsfaktor bearbeiten"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            type="number"
            inputMode="decimal"
            min={OPERATIONAL_RATING_FACTOR_MIN}
            max={OPERATIONAL_RATING_FACTOR_MAX}
            step={0.05}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Betriebsfaktor"
            className={`w-20 px-2 py-1 text-sm text-right border rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-primary-100 ${
              draftValid ? 'border-gray-300 dark:border-primary-500' : 'border-error-500 dark:border-error-400'
            }`}
          />
          <button
            type="button"
            disabled={!draftValid || saving}
            onClick={() => save(draftValue)}
            className="px-2.5 py-1 text-xs font-medium rounded bg-info-500 hover:bg-info-600 dark:bg-info-600 dark:hover:bg-info-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Speichern
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => setEditing(false)}
            className="px-2.5 py-1 text-xs font-medium rounded text-gray-600 dark:text-primary-300 hover:bg-gray-100 dark:hover:bg-primary-700"
          >
            Abbrechen
          </button>
          {factor != null && factor !== OPERATIONAL_RATING_FACTOR_DEFAULT && (
            <button
              type="button"
              disabled={saving}
              onClick={() => save(OPERATIONAL_RATING_FACTOR_DEFAULT)}
              className="px-2.5 py-1 text-xs font-medium rounded text-gray-600 dark:text-primary-300 hover:bg-gray-100 dark:hover:bg-primary-700"
            >
              Auf 1,0 zurücksetzen
            </button>
          )}
          {!draftValid && (
            <span className="w-full text-[11px] text-error-600 dark:text-error-400">
              Zulässig sind Werte von {fmt(OPERATIONAL_RATING_FACTOR_MIN, 1)} bis {fmt(OPERATIONAL_RATING_FACTOR_MAX, 1)}.
            </span>
          )}
        </div>
      )}

      {error && <div className="mt-1 text-[11px] text-error-600 dark:text-error-400">{error}</div>}

      <div className="mt-1.5 text-[11px] text-gray-500 dark:text-primary-400">
        Steuerungsauslöser ab {fmt(controlTriggerLoadingPct(editing && draftValid ? draftValue : effective), 0)} % {ratingOf}
        {' '}({fmt(CONTROL_TRIGGER_LOADING_PCT, 0)} % der betrieblichen Belastbarkeit). Compliance und Ampel bleiben beim Nennwert.
      </div>
    </div>
  );
};
